import React, { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import PropTypes, { func, number, string } from 'prop-types';

import { Theme } from '@constants';
import { useGetExpireTimeText } from '@hooks';

import { TimeUtils } from '../../../helpers/TimeUtils';
import { TWCTile } from '../../focusable/TWCTile/TWCTile';
import { TWCAlertIcon } from '../../primitives/TWCAlertIcon/TWCAlertIcon';
import { TWCText } from '../../primitives/TWCText/TWCText';

const tPath = 'currentWeatherConditionsPage.alertTile.';

/**
 * This component does not make an API call, but does some data processing
 * Using the same API/UI pattern so that Storybook can display the different variants of the UI component
 */
const AlertTileAPI = forwardRef(
  (
    {
      eventDescription,
      expireTimeUTC,
      issueTimeLocalTimeZone,
      onPress,
      testID,
      nextFocusLeft,
      nextFocusRight,
      nextFocusUp,
      nextFocusDown,
      onFocus,
      style,
      index,
      totalAlerts,
    },
    reference,
  ) => {
    const isTodayTomorrowOrLater = TimeUtils.isTodayTomorrowOrLater(expireTimeUTC);

    const expireTimeText = useGetExpireTimeText({
      expireTimeUTC,
      issueTimeLocalTimeZone,
      isTodayTomorrowOrLater,
      tPath,
    });

    return (
      <AlertTileUI
        eventDescription={eventDescription}
        expireTimeText={expireTimeText}
        index={index}
        nextFocusDown={nextFocusDown}
        nextFocusLeft={nextFocusLeft}
        nextFocusRight={nextFocusRight}
        nextFocusUp={nextFocusUp}
        ref={reference}
        style={style}
        testID={testID}
        totalAlerts={totalAlerts}
        onFocus={onFocus}
        onPress={onPress}
      />
    );
  },
);

AlertTileAPI.propTypes = {
  eventDescription: string.isRequired,
  expireTimeUTC: number.isRequired,
  issueTimeLocalTimeZone: string.isRequired,
  onPress: func.isRequired,
  testID: string.isRequired,
  index: number,
  totalAlerts: number,
  nextFocusDown: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusLeft: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusRight: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusUp: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  onFocus: PropTypes.func,
  style: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

AlertTileAPI.defaultProps = {
  index: null,
  totalAlerts: null,
  nextFocusLeft: null,
  nextFocusRight: null,
  nextFocusUp: null,
  nextFocusDown: null,
  onFocus: () => {},
  style: null,
};

const AlertTileUI = forwardRef(
  (
    {
      eventDescription,
      expireTimeText,
      onPress,
      testID,
      nextFocusLeft,
      nextFocusRight,
      nextFocusDown,
      nextFocusUp,
      onFocus,
      style,
      index,
      totalAlerts,
    },
    reference,
  ) => {
    const { t } = useTranslation();

    const inList = index !== undefined && totalAlerts !== undefined;
    const isLast = inList ? index === totalAlerts - 1 : true;
    const isSingle = inList ? totalAlerts === 1 : true;
    const hint = isLast ? t(`${tPath}accessibilityHintSingleTile`) : t(`${tPath}accessibilityHintMultiTiles`);
    const label = isSingle
      ? t(`${tPath}accessibilityLabelSingle`, {
          headline: eventDescription,
          expire: expireTimeText,
        })
      : t(`${tPath}accessibilityLabel`, {
          headline: eventDescription,
          expire: expireTimeText,
          index: index + 1,
          count: totalAlerts,
        });

    return (
      <TWCTile
        accessibilityHint={hint}
        accessibilityLabel={label}
        gradientType={'alertTile'}
        height={130}
        nextFocusDown={nextFocusDown}
        nextFocusLeft={nextFocusLeft}
        nextFocusRight={nextFocusRight}
        nextFocusUp={nextFocusUp}
        ref={reference}
        shouldUseContextReference={false}
        style={style}
        testID={testID}
        width={540}
        onFocus={onFocus}
        onPress={onPress}
      >
        <View style={styles.contentWrapper}>
          <View style={styles.iconWrapper}>
            <TWCAlertIcon eventDescription={eventDescription} height={60} width={60} />
          </View>
          <View style={styles.textWrapper}>
            <TWCText noTestID variant={'regularText30'}>
              {eventDescription}
            </TWCText>
            <TWCText noTestID variant={'regularText24Transparent'}>
              {expireTimeText}
            </TWCText>
          </View>
        </View>
      </TWCTile>
    );
  },
);

AlertTileUI.propTypes = {
  eventDescription: PropTypes.string.isRequired,
  expireTimeText: PropTypes.string.isRequired,
  onPress: PropTypes.func.isRequired,
  testID: PropTypes.string.isRequired,
  index: PropTypes.number,
  totalAlerts: PropTypes.number,
  nextFocusDown: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusLeft: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusRight: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusUp: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  onFocus: PropTypes.func,
  style: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

AlertTileUI.defaultProps = {
  index: null,
  totalAlerts: null,
  eventDescription: null,
  expireTimeText: null,
  onPress: () => {},
  testID: null,
  nextFocusLeft: null,
  nextFocusRight: null,
  nextFocusDown: null,
  nextFocusUp: null,
  onFocus: () => {},
  style: null,
};

export const AlertTile = {
  API: AlertTileAPI,
  UI: AlertTileUI,
};

const styles = StyleSheet.create({
  contentWrapper: {
    flexDirection: 'row',
    height: '100%',
  },
  iconWrapper: {
    justifyContent: 'center',
    paddingHorizontal: Theme.px(21),
  },
  textWrapper: {
    justifyContent: 'center',
  },
});
